import { useEffect, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { CheckCircle2, XCircle, Clock, Mail, Phone } from "lucide-react";

const STATUS_LABELS = { pending: "Pending", approved: "Approved", rejected: "Rejected" };
const STATUS_COLORS = {
  pending: "bg-amber-100 text-amber-800",
  approved: "bg-green-100 text-green-800",
  rejected: "bg-red-100 text-red-800",
};

export default function AdminCustomers() {
  const [customers, setCustomers] = useState([]);
  const [filter, setFilter] = useState("pending");
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = filter ? { status_filter: filter } : {};
      const { data } = await api.get("/admin/customers", { params });
      setCustomers(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load customers");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  const setStatus = async (id, status) => {
    try {
      await api.patch(`/admin/customers/${id}/status`, { status });
      toast.success(`Customer ${status}`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  };

  return (
    <div data-testid="admin-customers" className="mt-8">
      <div className="flex items-center gap-2 flex-wrap mb-5">
        {["pending", "approved", "rejected", ""].map((s) => (
          <button
            key={s || "all"}
            data-testid={`cust-filter-${s || "all"}`}
            onClick={() => setFilter(s)}
            className={`text-xs px-3 py-1.5 rounded-sm border transition-colors ${
              filter === s ? "bg-[#274e13] text-white border-[#274e13]" : "border-stone-300 text-stone-700 hover:bg-stone-100"
            }`}
          >
            {s ? STATUS_LABELS[s] : "All"}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-stone-500">Loading...</div>
      ) : customers.length === 0 ? (
        <div className="bg-white border border-stone-200 rounded-sm p-10 text-center text-stone-500">
          No customers in this list.
        </div>
      ) : (
        <div className="space-y-3">
          {customers.map((c) => (
            <div
              key={c.id}
              data-testid={`admin-cust-${c.id}`}
              className="bg-white border border-stone-200 rounded-sm p-5 grid grid-cols-1 md:grid-cols-12 gap-4 items-center"
            >
              <div className="md:col-span-4">
                <div className="font-semibold text-stone-900">{c.business_name}</div>
                <div className="text-xs text-stone-500 mt-0.5">{c.contact_name}</div>
                <div className="text-xs text-stone-500 flex items-center gap-1 mt-1">
                  <Phone size={11} /> {c.phone}
                </div>
              </div>
              <div className="md:col-span-3 text-xs">
                <div className="text-stone-500">GSTIN</div>
                <div className="font-mono">{c.gstin}</div>
              </div>
              <div className="md:col-span-3 text-xs text-stone-600 line-clamp-2">
                {c.address}
              </div>
              <div className="md:col-span-1 text-xs">
                <span className={`inline-block px-2 py-1 rounded-sm uppercase font-semibold tracking-wider ${STATUS_COLORS[c.status]}`}>
                  {c.status}
                </span>
                <div className="text-stone-500 mt-1">login: {c.login_id}</div>
              </div>
              <div className="md:col-span-1 flex flex-col gap-1.5">
                {c.status !== "approved" && (
                  <button
                    data-testid={`cust-approve-${c.id}`}
                    onClick={() => setStatus(c.id, "approved")}
                    className="text-xs px-2 py-1 bg-green-100 hover:bg-green-200 text-green-800 rounded-sm font-semibold inline-flex items-center justify-center gap-1"
                  >
                    <CheckCircle2 size={11} /> Approve
                  </button>
                )}
                {c.status !== "rejected" && (
                  <button
                    data-testid={`cust-reject-${c.id}`}
                    onClick={() => setStatus(c.id, "rejected")}
                    className="text-xs px-2 py-1 bg-red-100 hover:bg-red-200 text-red-800 rounded-sm font-semibold inline-flex items-center justify-center gap-1"
                  >
                    <XCircle size={11} /> Reject
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
